import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, X, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { API, errorText } from '../governance_v2/constants';

const MAX = 3;

/** value: [] = automatic (SUPER-100 decides at run time); otherwise the codes the user fixed. */
export const IADSelector = ({ companyId, query, value, onChange, testidPrefix = 'iad-selector' }) => {
  const [suggesting, setSuggesting] = useState(false);
  const { data } = useQuery({
    queryKey: ['iad-index', companyId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/iad/index`)).data,
    enabled: !!companyId, staleTime: 300000,
  });
  const index = data?.items || [];
  const names = Object.fromEntries(index.map((i) => [i.code, i.name]));
  const add = (code) => code && !value.includes(code) && value.length < MAX && onChange([...value, code]);

  const suggest = async () => {
    if (!query || query.trim().length < 3) return toast.error('Escribe primero la consulta');
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/companies/${companyId}/iad/preview`, { query });
      const codes = r.data.selections.map((s) => s.code);
      onChange(codes);
      toast.success(codes.length ? `SUPER-100 sugiere: ${codes.join(', ')}` : 'SUPER-100 no encontró un IAD aplicable');
    } catch (e) { toast.error(errorText(e)); } finally { setSuggesting(false); }
  };

  return (
    <div className="iad-scope space-y-2" data-testid={testidPrefix}>
      <div className="flex flex-wrap items-center gap-2">
        {!value.length && <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-xs text-amber-200" data-testid={`${testidPrefix}-auto`}>Automático · SUPER-100 elige</span>}
        {value.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 rounded-full border border-sky-400/40 bg-sky-400/10 px-3 py-1 text-xs text-sky-200" data-testid={`${testidPrefix}-chip-${c}`}>
            <span className="font-mono">{c}</span> · {names[c] || ''}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== c))} className="ml-1 opacity-70 transition-opacity hover:opacity-100" data-testid={`${testidPrefix}-remove-${c}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={suggest} disabled={suggesting} className="border-amber-400/40 text-amber-200 hover:bg-amber-400/10" data-testid={`${testidPrefix}-suggest`}>
          <Sparkles className="mr-1 h-3.5 w-3.5" />{suggesting ? 'Clasificando…' : 'Sugerir con IA'}
        </Button>
        <div className="relative">
          <Plus className="pointer-events-none absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <select value="" onChange={(e) => add(e.target.value)} disabled={value.length >= MAX}
            className="h-9 rounded-md border border-slate-600 bg-slate-900 pl-7 pr-3 text-xs text-slate-200 disabled:opacity-50" data-testid={`${testidPrefix}-add`}>
            <option value="">{value.length >= MAX ? `Máximo ${MAX}` : 'Elegir metodología…'}</option>
            {index.filter((i) => !value.includes(i.code)).map((i) => <option key={i.code} value={i.code}>{i.code} · {i.name} — {i.purpose}</option>)}
          </select>
        </div>
        {value.length > 0 && <button type="button" className="text-xs text-slate-400 underline-offset-2 hover:underline" onClick={() => onChange([])} data-testid={`${testidPrefix}-reset`}>Volver a automático</button>}
      </div>
    </div>
  );
};
